import { Badge, Table } from "@onengine/component-library";
import type { TableColumn, BadgeColor, BadgeVariant } from "@onengine/component-library";
import TableCellWithAvatar from "@/components/ui/TableCellWithAvatar/TableCellWithAvatar";
import { useFormBuilderList } from "@/hooks/form-builder/useFormBuilderList";
import type { FormBuilderListItem, FormBuilderLookup } from "@/api/form-builder/formBuilder.types";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { formatDateTime } from "@/utils/dateFormatter";
import {
  NewFormTemplateModal,
  type NewFormTemplateValues,
} from "@/components/features/FormBuilder/components/NewFormTemplateModal/NewFormTemplateModal";
import { Box, Flex, Group, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChecklist,
  IconFile,
  IconPlus,
  IconRefresh,
  IconRepeat,
  IconStack2,
  IconTool,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/Button/Button";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useMemo, type ReactNode } from "react";
import { useServerTable } from "@/hooks/table/useServerTable";
import { useBreakpointDown } from "@/hooks/useBreakpointDown";
import type { TableFilterColumnConfig } from "@/hooks/table/useTableFilterOptions";
import {
  InlineTableCell,
  renderTextCell,
  renderBadgeCell,
  TRUNCATE_STYLE,
} from "@/components/table/tableCells";
import { applyColumnFilters } from "@/components/table/columnFilters";
import { useIsServEngineSuperAdmin } from "@/hooks/form-builder/useIsServEngineSuperAdmin";
import { useEntityTenantStore } from "@/store/entityTenantStore";
import { useActiveSession } from "@/hooks/useActiveSession";
import { SUPER_ADMIN } from "@/components/data/constants";
import { FormRowActions } from "@/components/features/FormBuilder/components/FormRowActions/FormRowActions";
import { ACTIONS_COLUMN_ID, pinActionsLast } from "./pinActionsLast";
import styles from "./form-builder.module.scss";

export const Route = createFileRoute("/form-builder/_formBuilderLayout/templates/")({
  component: FormTemplatesPage,
  validateSearch: tableSearchSchema.parse,
});

const FORM_LIST_PAGE_SIZE = 25;

// Below this width the full table needs a sideways scroll to reach Status and the rest,
// so each row folds its key facts into the name cell instead.
const COMPACT_ROWS_BREAKPOINT = 1100;

// The stacked name cell grows past the table's fixed row height; the floor keeps a one-line
// row the same height as the other tables' rows (Table row height has no exported token).
const COMPACT_ROW_MIN_HEIGHT = 40;
const AUTO_HEIGHT_STYLE = { height: "auto", minHeight: COMPACT_ROW_MIN_HEIGHT };

// FormBuilderListItem is an interface; map it to an anonymous object type so it
// satisfies the Table's `T extends Record<string, unknown>` constraint.
type FormRow = { [K in keyof FormBuilderListItem]: FormBuilderListItem[K] };

const getStatusBadgeColors = (status: string): { color: BadgeColor; variant: BadgeVariant } => {
  switch (status.toLowerCase()) {
    case "published":
      return { color: "success", variant: "lowContrast" };
    case "draft":
      return { color: "warning", variant: "lowContrast" };
    default:
      return { color: "neutral", variant: "lowContrast" };
  }
};

const TenantLabel = ({
  row,
  size = "sm",
  className,
}: {
  row: FormRow;
  size?: "sm" | "xs";
  className?: string;
}) => (
  <>
    <Text component="span" size={size} className={className} style={TRUNCATE_STYLE}>
      {row.tenantName || "ServEngine"}
    </Text>
    {row.visibility === "ALL_TENANTS" && (
      <Badge color="iris" variant="lowContrast">
        Global
      </Badge>
    )}
  </>
);

const StatusBadge = ({ status }: { status: string }) => {
  const label = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  return <Badge {...getStatusBadgeColors(status)}>{label}</Badge>;
};

const WorkOrderTypeBadge = ({ label }: { label: string }) => (
  <Badge {...getWorkOrderTypeBadgeProps(label)}>{label}</Badge>
);

const getWorkOrderTypeBadgeProps = (
  label: string,
): { color: BadgeColor; variant: BadgeVariant; iconLeft: ReactNode } => {
  switch (label.toLowerCase()) {
    case "reactive":
      return { color: "orange", variant: "plain", iconLeft: <IconTool size={16} /> };
    case "pm":
      return { color: "pink", variant: "plain", iconLeft: <IconRepeat size={16} /> };
    case "surveys":
    case "survey":
      return { color: "cactus", variant: "plain", iconLeft: <IconChecklist size={16} /> };
    case "project":
    case "projects":
      return { color: "iris", variant: "plain", iconLeft: <IconStack2 size={16} /> };
    case "recurring":
      return { color: "pink", variant: "plain", iconLeft: <IconRefresh size={16} /> };
    default:
      return { color: "neutral", variant: "plain", iconLeft: <IconFile size={16} /> };
  }
};

const renderRowMeta = (row: FormRow): ReactNode => (
  <InlineTableCell rootStyle={AUTO_HEIGHT_STYLE}>
    <span className={styles.nameStack}>
      <Text component="span" size="sm" style={TRUNCATE_STYLE}>
        {row.name || "—"}
      </Text>
      <span className={styles.metaLine} data-testid="form-row-meta">
        <StatusBadge status={row.status || ""} />
        <Text component="span" size="xs" className={styles.metaText}>
          v{row.version}
        </Text>
      </span>
      <span className={styles.metaLine} data-testid="form-row-meta-secondary">
        {row.workOrderType?.label && <WorkOrderTypeBadge label={row.workOrderType.label} />}
        <TenantLabel row={row} size="xs" className={styles.metaText} />
      </span>
    </span>
  </InlineTableCell>
);

const getSortByField = (columnId: string): string | undefined => {
  const fieldMap: Record<string, string> = {
    name: "name",
    status: "status",
    version: "version",
    createdAt: "createdAt",
    updatedAt: "updatedAt",
  };
  return fieldMap[columnId];
};

const FORM_BUILDER_FILTER_COLUMNS: TableFilterColumnConfig[] = [
  {
    columnId: "name",
    filterByKey: "name",
    storeType: "forms",
    // The list API filters `name` by the form name string (it is not an id-keyed
    // filter), so send the label rather than the resolved form id.
    valueSource: "label",
    searchParams: { type: "FormBuilderForm", fields: ["name"], limit: 50, key: "formName" },
  },
  {
    columnId: "workOrderType",
    filterByKey: "workOrderTypeId",
    storeType: "forms",
    searchParams: { type: "LookupTypes", fields: ["label"], limit: 50, key: "workOrderType" },
  },
  {
    columnId: "formType",
    filterByKey: "formTypeId",
    storeType: "forms",
    searchParams: { type: "LookupTypes", fields: ["label"], limit: 50, key: "formType" },
  },
  {
    columnId: "trades",
    filterByKey: "tradeIds",
    storeType: "forms",
    searchParams: { type: "Trades", fields: ["name"], limit: 50, key: "trade" },
  },
  {
    columnId: "levels",
    filterByKey: "levelIds",
    storeType: "forms",
    searchParams: { type: "LookupTypes", fields: ["label"], limit: 50, key: "level" },
  },
  {
    columnId: "status",
    filterByKey: "status",
    storeType: "forms",
    searchParams: { type: "form_status", fields: ["label"], limit: 10, key: "status" },
  },
];

const renderChipList = (
  values: Array<{ key: string | number; label: string }>,
  color: BadgeColor,
): ReactNode => {
  if (values.length === 0) {
    return <span className={styles.placeholderCell}>—</span>;
  }
  return (
    <Box component="span" className={styles.chipGroup}>
      {values.map((value) => (
        <Badge key={value.key} color={color} variant="lowContrast">
          {value.label}
        </Badge>
      ))}
    </Box>
  );
};

export function FormTemplatesPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/form-builder/_formBuilderLayout/templates/" });
  const [newFormOpened, { open: openNewForm, close: closeNewForm }] = useDisclosure(false);
  const isServEngineSuperAdmin = useIsServEngineSuperAdmin();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const { userType } = useActiveSession();
  const isSuperAdmin = userType === SUPER_ADMIN;
  const isCompact = useBreakpointDown(COMPACT_ROWS_BREAKPOINT);

  const handleCreateForm = (values: NewFormTemplateValues) => {
    closeNewForm();
    navigate({
      to: "/form-builder/editor",
      search: {
        formName: values.name,
        formDescription: values.description,
        formTypeId: values.formTypeId,
        workOrderTypeId: values.workOrderTypeId,
        tradeIds: values.tradeIds.join(","),
        subTradeIds: values.subTradeIds.join(","),
        levelIds: values.levelIds.join(","),
        scopeAssignments: values.createAssignments
          ? {
              targets: values.scopeTargets,
              formType: values.assignmentFormType,
              workOrderType: values.assignmentWorkOrderType,
            }
          : undefined,
        visibility: values.isGlobal ? "ALL_TENANTS" : undefined,
        returnSearch: searchParams,
      },
    });
  };

  const { queryOptions, isPrefsLoading, controlProps, tableKey } = useServerTable({
    viewName: "formBuilderList",
    columns: FORM_BUILDER_FILTER_COLUMNS,
    getSortByField,
    enableUrlSync: true,
    searchParams,
    defaultStoreType: "forms",
    // Compact rows show one column; saving that layout would reorder the full table later.
    persistColumnPrefs: !isCompact,
    defaultPageSize: FORM_LIST_PAGE_SIZE,
  });

  const {
    data: formsResult,
    isFetching,
    isLoading,
    isError,
  } = useFormBuilderList({
    page: queryOptions.page,
    pageSize: queryOptions.pageSize,
    search: queryOptions.search,
    sortBy: queryOptions.sortBy,
    sortOrder: queryOptions.sortOrder,
    filters: queryOptions.filters,
  });

  const isInitialLoad = isLoading || isPrefsLoading;
  const emptyText = isInitialLoad
    ? "Loading forms…"
    : isError
      ? "Couldn't load forms. Please try again."
      : "No forms found";

  const tableData = useMemo<FormBuilderListItem[]>(() => formsResult?.data ?? [], [formsResult]);
  const pagination = formsResult?.pagination;

  const columns = useMemo<TableColumn<FormRow>[]>(() => {
    const allColumns = applyColumnFilters<FormRow>(
      [
        {
          key: "name",
          label: "Form Name",
          sortable: true,
          render: (row) => (isCompact ? renderRowMeta(row) : renderTextCell(row.name || "—")),
        },
        {
          key: "status",
          label: "Status",
          sortable: true,
          render: (row) => renderBadgeCell(<StatusBadge status={row.status || ""} />),
        },
        {
          key: "trades",
          label: "Trade",
          sortable: false,
          render: (row) =>
            renderBadgeCell(
              renderChipList(
                (row.trades ?? [])
                  .filter((trade) => trade.name)
                  .map((trade) => ({ key: trade.id, label: trade.name })),
                "indigo",
              ),
            ),
        },
        {
          key: "version",
          label: "Version",
          sortable: true,
          render: (row) => renderTextCell(`v${row.version}`),
        },
        ...(isServEngineSuperAdmin
          ? [
              {
                key: "tenantName",
                label: "Entity",
                sortable: false,
                render: (row: FormRow) => (
                  <InlineTableCell>
                    <TenantLabel row={row} />
                    {row.isFallbackDefault && (
                      <Badge color="iris" variant="lowContrast">
                        Fallback default
                      </Badge>
                    )}
                  </InlineTableCell>
                ),
              } satisfies TableColumn<FormRow>,
            ]
          : []),
        {
          key: "workOrderType",
          label: "Work Order Type",
          sortable: false,
          render: (row) => {
            const label = row.workOrderType?.label;
            if (!label) return renderTextCell("—");
            return renderBadgeCell(<WorkOrderTypeBadge label={label} />);
          },
        },
        {
          key: "formType",
          label: "Visit Type",
          sortable: false,
          render: (row) => renderTextCell(row.formType?.label || "—"),
        },
        {
          key: "subTrades",
          label: "Subtrade",
          sortable: false,
          render: (row) =>
            renderBadgeCell(
              renderChipList(
                (row.subTrades ?? [])
                  .filter((trade) => trade.name)
                  .map((trade) => ({ key: trade.id, label: trade.name })),
                "cyan",
              ),
            ),
        },
        {
          key: "levels",
          label: "Applies to",
          sortable: false,
          render: (row) =>
            renderBadgeCell(
              renderChipList(
                (row.levels ?? []).map((level: FormBuilderLookup) => ({
                  key: level.id,
                  label: level.label,
                })),
                "iris",
              ),
            ),
        },
        {
          key: "updatedAt",
          label: "Last Updated",
          sortable: true,
          render: (row) => renderTextCell(formatDateTime(row.updatedAt, true, true, true)),
        },
        {
          key: "createdAt",
          label: "Created On",
          sortable: true,
          render: (row) => renderTextCell(formatDateTime(row.createdAt, true, true, true)),
        },
        {
          key: "createdBy",
          label: "Created By",
          sortable: false,
          render: (row) => (
            <InlineTableCell>
              <TableCellWithAvatar name={row.createdBy?.name ?? ""} />
            </InlineTableCell>
          ),
        },
        {
          id: ACTIONS_COLUMN_ID,
          key: "id",
          label: "",
          sortable: false,
          pinRight: true,
          width: "60px",
          render: (row) => {
            const actingTenantId = entityTenant || row.tenantId;
            return (
              <FormRowActions
                form={row}
                tenantId={actingTenantId}
                canManagePlatformForms={isSuperAdmin && actingTenantId === row.tenantId}
              />
            );
          },
        },
      ],
      FORM_BUILDER_FILTER_COLUMNS,
    );
    // The name cell already carries status, version, type and entity in compact rows.
    return isCompact
      ? allColumns.filter((column) => column.key === "name" || column.id === ACTIONS_COLUMN_ID)
      : allColumns;
  }, [isServEngineSuperAdmin, isSuperAdmin, entityTenant, isCompact]);

  const savedColumnPrefs = useMemo(
    () =>
      pinActionsLast(
        controlProps.defaultColumnPrefs,
        columns.map((column) => column.id ?? String(column.key)),
      ),
    [controlProps.defaultColumnPrefs, columns],
  );

  return (
    <Flex direction="column" gap="var(--spacing-medium)">
      <Group justify="space-between" align="center">
        <Title
          order={3}
          style={{
            fontWeight: "var(--font-weight-bold)",
            fontSize: "var(--heading-font-size-h3)",
          }}
        >
          Form Templates
        </Title>
        <Button variant="primary" iconLeft={<IconPlus size={16} />} onClick={openNewForm}>
          New Form
        </Button>
      </Group>

      <NewFormTemplateModal
        opened={newFormOpened}
        onClose={closeNewForm}
        onSubmit={handleCreateForm}
        canFlagVisibility={isServEngineSuperAdmin}
        tenantId={entityTenant || undefined}
      />
      <Box className={styles.tableScroll}>
        <Table<FormRow>
          key={`${tableKey}-${isCompact ? "compact" : "wide"}`}
          {...controlProps}
          defaultColumnPrefs={savedColumnPrefs}
          columns={columns}
          data={tableData}
          loading={isFetching || isLoading || isPrefsLoading}
          totalCount={pagination?.total ?? 0}
          onRowClick={(row) =>
            navigate({
              to: "/form-builder/editor",
              search: { formId: row.id, tenantId: row.tenantId, returnSearch: searchParams },
            })
          }
          emptyText={emptyText}
        />
      </Box>
    </Flex>
  );
}
